import { useCallback, useRef } from 'react';

type ToneType = 'sine' | 'square' | 'sawtooth' | 'triangle';

export function useSensoryFeedback() {
  const audioCtxRef = useRef<AudioContext | null>(null);

  const initAudio = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
  }, []);

  const playTone = useCallback((frequency: number, type: ToneType, duration: number, volume = 0.1) => {
    initAudio();
    const ctx = audioCtxRef.current;
    if (!ctx) return;

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);

    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.05);
    gainNode.gain.setValueAtTime(volume, ctx.currentTime + duration - 0.05);
    gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + duration);

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.start();
    oscillator.stop(ctx.currentTime + duration);
  }, [initAudio]);

  const triggerHaptic = useCallback((pattern: number | number[]) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  }, []);

  const triggerInteraction = useCallback(() => {
    // Subtle physical tap
    triggerHaptic(5);
  }, [triggerHaptic]);

  const triggerWarning = useCallback(() => {
    triggerHaptic([100, 50, 100]);
    playTone(150, 'sine', 0.4, 0.1);
  }, [triggerHaptic, playTone]);

  const triggerCritical = useCallback(() => {
    triggerHaptic([200, 100, 200, 100, 500]);
    // Low frequency drone
    playTone(80, 'sawtooth', 1.0, 0.2);
  }, [triggerHaptic, playTone]);

  return { triggerInteraction, triggerWarning, triggerCritical, playTone, triggerHaptic };
}
